import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '../components/Navigation';
import HeroSection from '../components/HeroSection';
import VenueSection from '../components/CoHostSection';
import ActionSections from '../components/ActionSections';
import AboutSection from '../components/AboutSection';
import InfoSection from '../components/InfoSection';
import RegistrationSection from '../components/RegistrationSection';
import Footer from '../components/Footer';
import PreviewBanner from '../components/PreviewBanner';
import MediaGallerySection from '../components/MediaGallerySection';
import SpeakerPreviewSection from '../components/SpeakerPreviewSection';
import SponsorsSection from '../components/SponsorsSection';
import FeaturedNationSection from '../components/FeaturedNationSection';
import ProgramHighlightsSection from '../components/ProgramHighlightsSection';
import DocumentViewerSection from '../components/DocumentViewerSection';
import FieldEditor from '../components/FieldEditor';
import { DesignerSection, DESIGNER_SECTION_TYPES } from '../components/DesignerSections';
import {
  Loader2, Edit2, Eye, EyeOff, ChevronUp, ChevronDown,
  Save, X, Upload, ArrowLeft, Settings,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { usePreviewMode, fetchWithPreview } from '../hooks/usePreviewMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_RENDERERS = {
  hero:                  ({ content, config, section }) => <HeroSection content={content} videoConfig={config?.video} sectionId={section.id} />,
  about:                 ({ content, section }) => <AboutSection content={content} sectionId={section.id} />,
  action_cards:          ({ content, section }) => <ActionSections content={content} sectionId={section.id} />,
  stats:                 ({ content, section }) => <InfoSection content={content} sectionId={section.id} />,
  registration_cta:      ({ content, section }) => <RegistrationSection content={content} sectionId={section.id} />,
  location:              ({ content, section }) => <VenueSection content={content} sectionId={section.id} />,
  media_gallery:         ({ content, section }) => <MediaGallerySection content={content} sectionId={section.id} />,
  speaker_preview:       ({ content, section }) => <SpeakerPreviewSection content={content} sectionId={section.id} />,
  sponsors:              ({ content, section }) => <SponsorsSection content={content} sectionId={section.id} />,
  featured_nation:       ({ content, section }) => <FeaturedNationSection content={content} sectionId={section.id} />,
  program_highlights:    ({ content, section }) => <ProgramHighlightsSection content={content} sectionId={section.id} />,
  document_viewer:       ({ content, section }) => <DocumentViewerSection content={content} sectionId={section.id} />,
  feature_grid_diagonal: ({ section }) => <DesignerSection section={section} />,
  bento_layout:          ({ section }) => <DesignerSection section={section} />,
  step_timeline:         ({ section }) => <DesignerSection section={section} />,
  layered_panels:        ({ section }) => <DesignerSection section={section} />,
  layered_panels_6:      ({ section }) => <DesignerSection section={section} />,
  layered_panels_8:      ({ section }) => <DesignerSection section={section} />,
  custom_layout:         ({ section }) => <DesignerSection section={section} />,
};

/* ─── Inline edit overlay — wraps each section in preview mode ──── */
const InlineOverlay = ({ section, isEditing, onEdit, onToggle, onMove, isFirst, isLast, children }) => {
  const [hovered, setHovered] = useState(false);
  const show = hovered || isEditing;
  return (
    <div
      className={`relative ${!section.visible ? 'opacity-40' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`inline-section-${section.id}`}
    >
      {children}
      {show && (
        <div className="absolute top-2 left-2 right-2 z-[10000] pointer-events-none" data-testid={`inline-controls-${section.id}`}>
          <div className="flex items-center gap-2 pointer-events-auto">
            <div className="bg-[#00457f]/90 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
              <span>{section.name}</span>
              <span className="opacity-60 text-[10px]">{section.section_type}</span>
            </div>
            <button
              onClick={onEdit}
              className="bg-white/95 backdrop-blur-sm text-[#00457f] hover:bg-[#00457f] hover:text-white p-1.5 rounded-lg shadow-lg transition-colors"
              title="Edit section"
              data-testid={`inline-edit-btn-${section.id}`}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onToggle}
              className={`backdrop-blur-sm p-1.5 rounded-lg shadow-lg transition-colors ${
                section.visible ? 'bg-white/95 text-green-600 hover:bg-green-50' : 'bg-red-50 text-red-500'
              }`}
              title={section.visible ? 'Hide section' : 'Show section'}
              data-testid={`inline-vis-btn-${section.id}`}
            >
              {section.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
            {!isFirst && (
              <button
                onClick={() => onMove('up')}
                className="bg-white/95 backdrop-blur-sm text-slate-600 hover:bg-slate-100 p-1.5 rounded-lg shadow-lg transition-colors"
                title="Move up"
                data-testid={`inline-up-btn-${section.id}`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
            )}
            {!isLast && (
              <button
                onClick={() => onMove('down')}
                className="bg-white/95 backdrop-blur-sm text-slate-600 hover:bg-slate-100 p-1.5 rounded-lg shadow-lg transition-colors"
                title="Move down"
                data-testid={`inline-down-btn-${section.id}`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
      {show && (
        <div
          className="absolute inset-0 z-[9999] pointer-events-none rounded-sm"
          style={{ outline: isEditing ? '2px solid #00457f' : '2px dashed #00457f55' }}
        />
      )}
    </div>
  );
};

/* ─── Slide-out editor panel ──── */
const InlineEditorPanel = ({ section, template, content, onChange, onSave, onClose, saving }) => (
  <div className="fixed top-0 right-0 w-full max-w-md h-full bg-white shadow-2xl z-[10001] flex flex-col border-l" data-testid="inline-editor-panel">
    <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
      <div className="min-w-0">
        <h2 className="font-bold text-base text-slate-900 truncate">{section.name}</h2>
        <p className="text-xs text-slate-500">{section.section_type}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={onClose} data-testid="inline-editor-cancel">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="inline-editor-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </div>
    </div>
    <div className="flex-1 overflow-y-auto p-4 space-y-5">
      {template?.fields?.map((field) => (
        <FieldEditor
          key={field.name}
          field={field}
          value={content[field.name]}
          onChange={onChange}
        />
      ))}
      {(!template || !template.fields?.length) && (
        <p className="text-sm text-slate-500 italic">No editable fields for this section type.</p>
      )}
    </div>
  </div>
);

/* ─── Floating admin toolbar (bottom of preview) ──── */
const PreviewToolbar = ({ onPublish, publishing, hasChanges }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[45] flex items-center gap-3 bg-[#0a1a2f]/95 backdrop-blur-md text-white px-5 py-3 rounded-2xl shadow-2xl border border-white/10" data-testid="preview-toolbar">
    <a href="/admin/homepage" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors" data-testid="toolbar-back-admin">
      <ArrowLeft className="w-3.5 h-3.5" /> Admin Panel
    </a>
    <div className="w-px h-5 bg-white/20" />
    <a href="/admin/homepage" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors" data-testid="toolbar-page-settings">
      <Settings className="w-3.5 h-3.5" /> Page Settings
    </a>
    <div className="w-px h-5 bg-white/20" />
    <button
      onClick={onPublish}
      disabled={publishing}
      className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-1.5 rounded-xl transition-colors"
      data-testid="toolbar-publish"
    >
      {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
      Publish
    </button>
  </div>
);

/* ─── Main page component ──── */
const LandingPage = () => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [config, setConfig] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [draftMeta, setDraftMeta] = useState(null);
  const { preview, isAdmin } = usePreviewMode();
  const inlineMode = preview && isAdmin;

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState({});
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    let ignore = false;
    const fetchCMSData = async () => {
      try {
        const { data, isDraft: draft } = await fetchWithPreview(`${API_URL}/api/homepage/public`, { previewEnabled: preview });
        if (ignore) return;
        if (data) {
          setSections(data.sections || []);
          setConfig(data.config || null);
        }
        setIsDraft(draft);
        setDraftMeta(draft ? { updated_at: new Date().toISOString() } : null);
      } catch (error) {
        console.error('Failed to fetch homepage CMS data:', error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchCMSData();
    return () => { ignore = true; };
  }, [preview]);

  // Fetch templates for field definitions (only in inline mode)
  useEffect(() => {
    if (!inlineMode) return;
    fetch(`${API_URL}/api/homepage/templates`)
      .then(r => r.json())
      .then(setTemplates)
      .catch(() => {});
  }, [inlineMode]);

  const getTemplate = useCallback((section) => {
    const key = section.template_id || `tpl_${section.section_type}`;
    return templates.find(t => t.id === key || t.section_type === section.section_type);
  }, [templates]);

  const startEditing = (section) => {
    setEditingId(section.id);
    setEditContent(section.draft_content || section.content || {});
  };

  const handleFieldChange = (fieldName, value) => {
    setEditContent(prev => ({ ...prev, [fieldName]: value }));
    // Optimistic local update for live preview
    setSections(prev => prev.map(s =>
      s.id === editingId ? { ...s, content: { ...(s.content || {}), [fieldName]: value } } : s
    ));
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/homepage/sections/${editingId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Save failed (${res.status})`);
      }
      const updated = await res.json();
      setSections(prev => prev.map(s => s.id === editingId ? { ...updated, content: editContent } : s));
      toast.success('Section saved');
      setEditingId(null);
      setEditContent({});
    } catch (e) {
      toast.error(e.message || 'Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    // Revert optimistic update — refetch the section's original content
    const original = sections.find(s => s.id === editingId);
    if (original) {
      setSections(prev => prev.map(s =>
        s.id === editingId ? { ...s, content: s.draft_content || original.content } : s
      ));
    }
    setEditingId(null);
    setEditContent({});
  };

  const toggleVisibility = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const newVisible = !section.visible;
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, visible: newVisible } : s));
    try {
      await fetch(`${API_URL}/api/homepage/sections/${sectionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ visible: newVisible }),
      });
      toast.success(newVisible ? 'Section visible' : 'Section hidden');
    } catch {
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, visible: !newVisible } : s));
      toast.error('Failed to toggle visibility');
    }
  };

  const moveSection = async (sectionId, direction) => {
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const orders = reordered.map((s, i) => ({ id: s.id, order: i }));
    setSections(reordered);
    try {
      await fetch(`${API_URL}/api/homepage/sections/reorder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orders),
      });
    } catch {
      toast.error('Failed to reorder');
    }
  };

  const publishAll = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/homepage/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ change_notes: 'Published from inline editor' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        // Refresh sections to get published state
        const { data: refreshed } = await fetchWithPreview(`${API_URL}/api/homepage/public`, { previewEnabled: true });
        if (refreshed) {
          setSections(refreshed.sections || []);
          setIsDraft(false);
        }
      } else {
        toast.error(data.message || 'Publish failed');
      }
    } catch {
      toast.error('Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
      </div>
    );
  }

  const visibleSections = inlineMode ? sections : sections.filter(s => s.visible !== false);
  const editingSection = editingId ? sections.find(s => s.id === editingId) : null;
  const editingTemplate = editingSection ? getTemplate(editingSection) : null;

  return (
    <div className={`min-h-screen bg-white ${inlineMode ? 'pb-20' : ''}`}>
      <PreviewBanner isDraft={isDraft} draftMeta={draftMeta} pageLabel="Homepage" />
      <Navigation />

      {visibleSections.map((section, idx) => {
        const Renderer = SECTION_RENDERERS[section.section_type];
        if (!Renderer) return null;
        const sectionContent = editingId === section.id ? editContent : section.content;
        const renderedSection = Renderer({
          content: sectionContent,
          section: { ...section, content: sectionContent },
          config,
        });

        if (!inlineMode) {
          return <React.Fragment key={section.id}>{renderedSection}</React.Fragment>;
        }

        return (
          <InlineOverlay
            key={section.id}
            section={section}
            isEditing={editingId === section.id}
            onEdit={() => startEditing(section)}
            onToggle={() => toggleVisibility(section.id)}
            onMove={(dir) => moveSection(section.id, dir)}
            isFirst={idx === 0}
            isLast={idx === visibleSections.length - 1}
          >
            {renderedSection}
          </InlineOverlay>
        );
      })}

      <Footer />

      {/* Inline editor slide-out */}
      {inlineMode && editingId && editingSection && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[10000]" onClick={cancelEditing} />
          <InlineEditorPanel
            section={editingSection}
            template={editingTemplate}
            content={editContent}
            onChange={handleFieldChange}
            onSave={saveSection}
            onClose={cancelEditing}
            saving={saving}
          />
        </>
      )}

      {/* Floating admin toolbar */}
      {inlineMode && !editingId && (
        <PreviewToolbar
          onPublish={publishAll}
          publishing={publishing}
        />
      )}
    </div>
  );
};

export default LandingPage;
